import { useEffect, useMemo } from "react";
import { formatFitmentLabel } from "../lib/catalog";
import { imageUrl } from "../lib/images";
import { productMetaDescription, productSearchDescription } from "../lib/product-copy";
import { pathnameForCategory, pathnameForProduct, type AppRoute } from "../lib/routes";
import type { Product } from "../types/catalog";

type SeoProps = {
  activeRoute: AppRoute;
  curatedProducts: Product[];
  routeCategory: string;
  product: Product | null;
};

type SeoMeta = {
  title: string;
  description: string;
  canonicalPath: string;
  robots: string;
  image: string;
  schema: unknown[];
};

const siteOrigin = "https://truefitautomotivesolutions.co.ke";
const siteName = "Truefit Automotive Solutions";
const defaultImage = `${siteOrigin}/truefit-logo-dark.svg`;

function absoluteUrl(pathname: string) {
  return `${siteOrigin}${pathname}`;
}

function setMetaByName(name: string, content: string) {
  let element = document.head.querySelector<HTMLMetaElement>(`meta[name="${name}"]`);

  if (!element) {
    element = document.createElement("meta");
    element.name = name;
    document.head.appendChild(element);
  }

  element.content = content;
}

function setMetaByProperty(property: string, content: string) {
  let element = document.head.querySelector<HTMLMetaElement>(`meta[property="${property}"]`);

  if (!element) {
    element = document.createElement("meta");
    element.setAttribute("property", property);
    document.head.appendChild(element);
  }

  element.content = content;
}

function setCanonical(href: string) {
  let element = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');

  if (!element) {
    element = document.createElement("link");
    element.rel = "canonical";
    document.head.appendChild(element);
  }

  element.href = href;
}

function updateStructuredData(schema: unknown[]) {
  let element = document.head.querySelector<HTMLScriptElement>("#route-schema");

  if (!element) {
    element = document.createElement("script");
    element.id = "route-schema";
    element.type = "application/ld+json";
    document.head.appendChild(element);
  }

  element.textContent = JSON.stringify(schema);
}

function truncate(value: string, maxLength: number) {
  if (value.length <= maxLength) {
    return value;
  }

  return `${value.slice(0, maxLength - 1).trim()}...`;
}

function productSchema(product: Product) {
  const fitment = formatFitmentLabel(product);
  const productImage = imageUrl(product.imageStorageKey, "productDetail");

  return {
    "@context": "https://schema.org",
    "@type": "Product",
    name: product.name,
    sku: product.sku,
    brand: {
      "@type": "Brand",
      name: product.brand || "Truefit Automotive Solutions",
    },
    category: product.category,
    description: productSearchDescription(product),
    image: productImage ? [productImage] : undefined,
    additionalProperty: fitment
      ? [
          {
            "@type": "PropertyValue",
            name: "Vehicle fitment",
            value: fitment,
          },
        ]
      : undefined,
    offers: {
      "@type": "Offer",
      availability:
        product.stockQty > 0
          ? "https://schema.org/InStock"
          : "https://schema.org/OutOfStock",
      price: product.priceKes,
      priceCurrency: "KES",
      url: absoluteUrl(pathnameForProduct(product.id)),
    },
  };
}

function siteSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "AutoPartsStore",
    name: siteName,
    url: absoluteUrl("/"),
    logo: defaultImage,
    areaServed: {
      "@type": "Country",
      name: "Kenya",
    },
    currenciesAccepted: "KES",
  };
}

function itemListSchema(products: Product[], url: string) {
  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    itemListElement: products.slice(0, 12).map((product, index) => ({
      "@type": "ListItem",
      position: index + 1,
      url: absoluteUrl(pathnameForProduct(product.id)),
      name: product.name,
    })),
    url,
  };
}

function seoMeta({
  activeRoute,
  curatedProducts,
  product,
  routeCategory,
}: SeoProps): SeoMeta {
  if (activeRoute === "product") {
    if (!product) {
      return {
        title: `Product not found | ${siteName}`,
        description: "This Truefit Automotive Solutions product is no longer available.",
        canonicalPath: "/catalog",
        robots: "noindex, follow",
        image: defaultImage,
        schema: [siteSchema()],
      };
    }

    const fitment = formatFitmentLabel(product);
    const productImage = imageUrl(product.imageStorageKey, "productDetail") || defaultImage;

    return {
      title: truncate(`${product.name}${fitment ? ` for ${fitment}` : ""} | ${siteName}`, 70),
      description: productMetaDescription(product),
      canonicalPath: pathnameForProduct(product.id),
      robots: "index, follow",
      image: productImage,
      schema: [siteSchema(), productSchema(product)],
    };
  }

  if (activeRoute === "category") {
    const categoryName = routeCategory || "Auto parts";

    return {
      title: `${categoryName} in Kenya | ${siteName}`,
      description: truncate(
        `Shop ${categoryName.toLowerCase()} from Truefit Automotive Solutions in Kenya. Browse auto spares by brand, SKU, and vehicle fitment.`,
        155
      ),
      canonicalPath: pathnameForCategory(categoryName),
      robots: "index, follow",
      image: defaultImage,
      schema: [siteSchema(), itemListSchema(curatedProducts, absoluteUrl(pathnameForCategory(categoryName)))],
    };
  }

  if (activeRoute !== "catalog") {
    return {
      title: `${siteName} | Auto Parts in Kenya`,
      description: "Customer checkout and order pages for Truefit Automotive Solutions.",
      canonicalPath: "/catalog",
      robots: "noindex, nofollow",
      image: defaultImage,
      schema: [siteSchema()],
    };
  }

  return {
    title: `${siteName} | Auto Parts in Kenya`,
    description:
      "Find genuine auto spares in Kenya by product, brand, SKU, and vehicle fitment with Truefit Automotive Solutions.",
    canonicalPath: "/",
    robots: "index, follow",
    image: defaultImage,
    schema: [siteSchema(), itemListSchema(curatedProducts, absoluteUrl("/catalog"))],
  };
}

export function Seo({ activeRoute, curatedProducts, product, routeCategory }: SeoProps) {
  const meta = useMemo(
    () => seoMeta({ activeRoute, curatedProducts, product, routeCategory }),
    [activeRoute, curatedProducts, product, routeCategory]
  );
  const canonicalUrl = absoluteUrl(meta.canonicalPath);

  useEffect(() => {
    document.title = meta.title;
    setMetaByName("description", meta.description);
    setMetaByName("robots", meta.robots);
    setMetaByName("twitter:card", "summary");
    setMetaByName("twitter:title", meta.title);
    setMetaByName("twitter:description", meta.description);
    setMetaByName("twitter:image", meta.image);
    setMetaByProperty("og:site_name", siteName);
    setMetaByProperty("og:type", activeRoute === "product" ? "product" : "website");
    setMetaByProperty("og:title", meta.title);
    setMetaByProperty("og:description", meta.description);
    setMetaByProperty("og:url", canonicalUrl);
    setMetaByProperty("og:image", meta.image);
    setCanonical(canonicalUrl);
    updateStructuredData(meta.schema);
  }, [activeRoute, canonicalUrl, meta]);

  return null;
}
